import React, { useEffect, useRef, useState, useCallback } from 'react';
import '../styles/tradeviewer.css';
import OrderModal from './OrderModal';
import { FaAngleLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

function TradeViewer() {
    const navigate = useNavigate();
    const chartRef = useRef(null);
    const scriptRef = useRef(null); // Track the script element
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState('buy');
    const [initialBalance, setInitialBalance] = useState(0.00);
    const [chartHeight, setChartHeight] = useState(700);
    const [tradeData, setTradeData] = useState([]);
    const [userData, setUserData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // Interval ID for auto-reload
    const [intervalId, setIntervalId] = useState(null);

    const handleAmountClick = () => {
        navigate('/tradehistory'); // Navigate to Trade History page
    };


    const handleBuyClick = () => {
        setModalType('buy');
        setIsModalOpen(true);
    };

    const handleSellClick = () => {
        setModalType('sell');
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const getTradeIcon = (pair) => {
        const iconMap = {
            BTCUSD: '/icons/Bitcoin.png',
            ETHUSD: '/icons/Ethereum.png',
            XRPUSD: '/icons/xrp-logo.jpg',
            SHIBUSD: '/icons/shib.png',
        };
        return iconMap[pair] || '/icons/Bitcoin.png';
    };

    const fetchUserData = useCallback(() => {
        try {
            const storedUser = localStorage.getItem("user");
            if (storedUser) {
                const user = JSON.parse(storedUser);
                setUserData(user);
            } else {
                setError("No user data found in localStorage.");
            }
        } catch (error) {
            console.error("Error in fetchUserData:", error);
            setError("Failed to retrieve user data: " + error.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchTradeData = useCallback(async () => {
        if (!userData?.username) {
            setError("Username is not available.");
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        const encodedUsername = encodeURIComponent(userData.username);
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const url = `${baseUrl}/orders/username/${encodedUsername}`;

        try {
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            const transformedData = data.orders.map((order) => {
                const profitLoss = parseFloat(order.profitLossAmount) || 0;
                return {
                    id: order.id,
                    pair: `${order.pair.base_currency}${order.pair.quote_currency}`,
                    type: order.side === 'buy' ? 'Buy' : 'Sell',
                    priceRange: `${order.price || 'N/A'} - ${order.lastPrice || 'N/A'}`,
                    timestamp: order.created_at
                        ? new Date(order.created_at).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        }).replace(/,/, '')
                        : 'N/A',
                    amount: profitLoss >= 0 ? `+${profitLoss.toFixed(2)}` : profitLoss.toFixed(2),
                    isNegative: profitLoss < 0,
                    isPositive: profitLoss > 0,
                };
            });

            setTradeData(transformedData.slice(0, 5)); // Show only first 5 rows
            setError(null);
        } catch (err) {
            console.error("Fetch error:", err);
            setError(err.message || "Failed to fetch trade history");
            setTradeData([]);
        } finally {
            setIsLoading(false);
        }
    }, [userData]);

    const reloadTradeData = useCallback(() => {
        fetchTradeData();
    }, [fetchTradeData]);

    // Set up auto-reload
    useEffect(() => {
        const startAutoReload = () => {
            if (userData?.username && !intervalId) {
                const id = setInterval(fetchTradeData, 5000); // Reload every 5 seconds
                setIntervalId(id);
                console.log("Auto-reload started with interval ID:", id);
            }
        };

        startAutoReload();

        // Cleanup on unmount or when userData changes
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
                console.log("Auto-reload cleared with interval ID:", intervalId);
                setIntervalId(null);
            }
        };
    }, [userData, intervalId, fetchTradeData]);


    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 767) setChartHeight(400);
            else if (window.innerWidth <= 1023) setChartHeight(600);
            else setChartHeight(700);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    useEffect(() => {
        if (userData?.username) {
            fetchTradeData();
        }
    }, [userData, fetchTradeData]);

    useEffect(() => {
        if (chartRef.current && !scriptRef.current) {
            console.log("Initializing TradingView widget, chartRef:", chartRef.current);

            // Create widget container if not already present
            let container = chartRef.current.querySelector('.tradingview-widget-container__widget');
            if (!container) {
                container = document.createElement("div");
                container.className = "tradingview-widget-container__widget";
                chartRef.current.appendChild(container);
            }

            // Create and append the script only once
            const script = document.createElement("script");
            script.src = "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
            script.type = "text/javascript";
            script.async = true;
            script.innerHTML = JSON.stringify({
                width: "100%",
                height: chartHeight,
                symbol: "BINANCE:BTCUSDT",
                interval: "1",
                timezone: "Etc/UTC",
                theme: "dark",
                style: "1",
                locale: "en",
                enable_publishing: false,
                hide_top_toolbar: false,
                hide_legend: false,
                allow_symbol_change: true,
                save_image: false,
                studies: [],
                show_popup_button: true,
                popup_width: "1000",
                popup_height: "650",
                realtime: true,
            });

            script.onload = () => {
                console.log("TradingView script loaded successfully");
            };
            script.onerror = (e) => {
                console.error("Failed to load TradingView script:", e);
            };

            chartRef.current.appendChild(script);
            scriptRef.current = script; // Store script reference

            console.log("Script appended to DOM");
        }

        return () => {
            // Cleanup: Remove script but keep container
            if (scriptRef.current && chartRef.current) {
                console.log("Cleaning up TradingView script");
                chartRef.current.removeChild(scriptRef.current);
                scriptRef.current = null;
            }
        };
    }, []); // Empty dependency array to run only on mount/unmount

    const handleBackClick = () => {
        navigate(-1);
    };

    return (
        <div className="Trade-page">
            <nav className="navbar">
                <div className="navbar-left">
                    <FaAngleLeft
                        className="icon"
                        onClick={handleBackClick}
                        style={{ cursor: 'pointer' }}
                    />
                    <div className="navbar-logo">Trade</div>
                </div>
            </nav>
            <div className="Trade-container">
                <div className="chart-container">
                    <div className="chart-header">
                        <span className="Crypto">BTCUSD</span>
                    </div>
                    <div className="tradingview-widget-container" ref={chartRef}></div>
                    <div className="trade-history-container">
                        <table className="trade-table">
                        <thead>
                                <tr>
                                    <th>Last Trade</th>
                                    <th
                                        onClick={handleAmountClick}
                                        style={{ cursor: 'pointer', color: '#8329f8', fontWeight:'normal' }}
                                    >
                                        View more
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {tradeData.map((trade, index) => (
                                    <tr key={index} className={`trade-row ${index % 2 === 0 ? 'alt-row' : ''}`}>
                                        <td>
                                            <div className="trade-header">
                                                <span className="trade-pair">{trade.pair}</span>
                                                <span className={`trade-type ${trade.type === 'Buy' ? 'buy-type' : 'sell-type'}`}>
                                                    {trade.type}
                                                </span>
                                            </div>
                                            <div className="trade-details">
                                                {trade.priceRange}, {trade.timestamp}
                                            </div>
                                        </td>
                                        <td
                                            className={`trade-amount ${trade.isNegative ? 'negative-amount' : trade.isPositive ? 'positive-amount' : ''}`}
                                            style={{ color: trade.isNegative ? '#d32f2f' : trade.isPositive ? '#28a745' : 'inherit' }}
                                        >
                                            {trade.amount}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div className="footer-actions">
                <button className="footer-button sell-button" onClick={handleSellClick}>
                    Sell
                </button>
                <button className="footer-button buy-button" onClick={handleBuyClick}>
                    Buy
                </button>
            </div>

            <OrderModal
                isOpen={isModalOpen}
                onClose={closeModal}
                type={modalType}
                initialBalance={initialBalance}
                onTradeComplete={reloadTradeData}
            />
        </div>
    );
}

export default TradeViewer;